import { legalDocumentEditionDateSchema } from "@ai-crm/shared";
import type { AdminLegalDocumentVersionDTO, LegalDocumentKey } from "@ai-crm/shared";
import { useId, useState } from "react";
import type { FormEvent } from "react";
import { adminApi } from "../lib/api";
import { usePaymentDocuments } from "../lib/payment-documents";
import { paymentButton, paymentPrimary, usePaymentScope } from "../lib/payment-ui";
import { useLegalDocumentWrite } from "../lib/use-legal-documents";
import { ConfirmDialog } from "./confirm-dialog";

interface DocumentVersionFormProps {
  documentKey: LegalDocumentKey;
  title: string;
  revision: number;
  catalogReady: boolean;
  activeEdition: string | null;
  initialDraft: AdminLegalDocumentVersionDTO | null;
  knownPublished: boolean;
  onUploaded: (version: AdminLegalDocumentVersionDTO) => void;
  onPublished: () => void;
  onClose: () => void;
}

function moscowToday() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Moscow", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function DocumentVersionForm({
  documentKey, title, revision, catalogReady, activeEdition, initialDraft,
  knownPublished, onUploaded, onPublished, onClose,
}: DocumentVersionFormProps) {
  const scope = usePaymentScope();
  const write = useLegalDocumentWrite(documentKey);
  const docs = usePaymentDocuments();
  const id = useId();
  const [file, setFile] = useState<File | null>(null);
  const [editionDate, setEditionDate] = useState("");
  const [draft, setDraft] = useState(initialDraft);
  const [previewRevision, setPreviewRevision] = useState<number | null>(null);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState(false);
  const [operation, setOperation] = useState<"upload" | "publish" | null>(null);
  const locked = write.locked || docs.busy;
  const published = knownPublished || Boolean(draft?.publishedAt);

  function changed() {
    setError(null);
    write.clearError();
  }
  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!file || locked) return;
    if (!legalDocumentEditionDateSchema.safeParse(editionDate).success || editionDate > moscowToday()) {
      setError("Укажите существующую дату редакции не позднее сегодняшнего дня по Москве.");
      return;
    }
    if (file.size === 0 || file.size > 10 * 1024 * 1024) {
      setError("Выберите PDF размером до 10 МиБ.");
      return;
    }
    setError(null);
    setOperation("upload");
    await write.upload(file, { requestId: crypto.randomUUID(), editionDate }, (version) => {
      setDraft(version);
      setPreviewRevision(null);
      onUploaded(version);
    });
  }
  async function preview(open: boolean) {
    if (!draft || !scope.token || locked || !catalogReady) return;
    const guard = scope.capture(), version = draft, expectedRevision = revision;
    const ok = await docs.fetchDocument(
      () => adminApi.legalDocumentVersionPdf(scope.token!, documentKey, version.id),
      `${documentKey}-${version.editionDate}.pdf`, open,
    );
    if (guard.isCurrent() && ok) {
      setPreviewRevision(expectedRevision);
      setConflict(false);
      write.clearError();
    }
  }
  async function publish() {
    if (!draft || previewRevision === null || locked || published || !catalogReady) return;
    setDialog(false);
    setOperation("publish");
    await write.publish({
      requestId: crypto.randomUUID(), versionId: draft.id, expectedRevision: previewRevision,
    }, () => {
      setDraft(null);
      setPreviewRevision(null);
      onPublished();
    }, () => {
      // A confirmed conflict retires this intent. A new explicit preview creates a new request.
      setConflict(true);
      setPreviewRevision(null);
    });
  }
  return (
    <section className="space-y-4 rounded-md border p-3" aria-label={`Редакция: ${title}`} aria-busy={locked}>
      <h3 className="font-semibold">{draft ? "Проверка редакции" : "Загрузить редакцию"}</h3>
      {!draft && (
        <form className="space-y-3" onSubmit={(event) => void upload(event)}>
          <label className="block space-y-1 text-sm" htmlFor={`${id}-file`}>
            <span>Файл PDF</span>
            <input id={`${id}-file`} type="file" accept="application/pdf,.pdf" required
              className="control h-auto py-2" disabled={locked}
              onChange={(event) => { setFile(event.target.files?.[0] ?? null); changed(); }} />
          </label>
          <label className="block space-y-1 text-sm" htmlFor={`${id}-date`}>
            <span>Дата редакции</span>
            <input id={`${id}-date`} className="control" type="date" value={editionDate}
              max={moscowToday()} required disabled={locked}
              onChange={(event) => { setEditionDate(event.target.value); changed(); }} />
          </label>
          <p className="text-sm text-muted-foreground">PDF до 10 МиБ. Загрузка сохраняет черновик; публикация выполняется отдельно.</p>
          <button className={paymentPrimary} disabled={locked || !file || !editionDate} type="submit">Загрузить PDF</button>
        </form>
      )}
      {draft && (
        <div className="space-y-3">
          <p className="text-sm">Дата редакции: {draft.editionDate}</p>
          {published ? <p>Эта редакция уже опубликована.</p> : <p className="text-sm">Откройте или скачайте PDF и проверьте содержание перед публикацией.</p>}
          {conflict && (
            <p role="alert" className="text-sm">Действующая редакция изменилась. Черновик сохранён. Сейчас: {activeEdition ?? "нет публикации"}. Проверьте актуальную редакцию в карточке и повторно откройте PDF черновика.</p>
          )}
          <div className="flex flex-wrap gap-2">
            <button className={paymentButton} disabled={locked || !catalogReady} onClick={() => void preview(true)}>
              {conflict ? "Повторно проверить PDF" : "Открыть PDF черновика"}
            </button>
            <button className={paymentButton} disabled={locked || !catalogReady} onClick={() => void preview(false)}>Скачать PDF черновика</button>
          </div>
          {!published && !write.uncertain && (
            <button className={paymentPrimary} disabled={locked || previewRevision === null || !catalogReady}
              onClick={() => setDialog(true)}>Опубликовать</button>
          )}
        </div>
      )}
      {dialog && draft && (
        <ConfirmDialog title={`Опубликовать: ${title}`}
          description={`Редакция от ${draft.editionDate} начнёт действовать сразу после публикации. Проверьте дату и содержание PDF.`}
          confirmLabel="Опубликовать сейчас" isPending={write.pending}
          confirmDisabled={published || !catalogReady || previewRevision === null}
          onCancel={() => setDialog(false)} onConfirm={() => void publish()} />
      )}
      {write.uncertain && (
        <button className={paymentPrimary} disabled={write.pending} onClick={() => void write.retry()}>
          {operation === "upload" ? "Повторить ту же загрузку" : "Повторить ту же публикацию"}
        </button>
      )}
      {(error || write.error || docs.error) && <p role="alert" className="text-sm text-danger">{error ?? write.error ?? docs.error}</p>}
      <button className={paymentButton} disabled={locked} onClick={onClose}>Закрыть форму</button>
    </section>
  );
}
